/**
 * Glowwa PDP — Seal Subscriptions DOM enhancements.
 * Cart/selling plans: Seal (data-product + form inputs).
 * Copy: Seal plan name/description when present; theme block glowwa-seal-pdp-config for cap/benefits/OTP subline.
 *
 * Visibility: purchase options show as soon as Seal injects (CSS targets native Seal selectors).
 * This script adds polish (cap, benefits, OTP subline) — it must never block checkout.
 */
(function glowwaSealEnhance() {
  /** @type {MutationObserver[]} */
  const observers = [];
  /** @type {Set<HTMLElement>} */
  const observedRoots = new Set();
  /** @type {number | null} */
  let debounceTimer = null;
  /** @type {number | null} */
  let sealPollTimer = null;
  let isEnhancing = false;

  const SEAL_SCRIPT_MATCH = 'sealsubscriptions.js';
  const BUY_AREA_SELECTOR =
    '.glowwa-pdp-buy-area--subscription, [data-glowwa-buy-mode="subscription"]';

  const DEFAULT_BENEFITS = [
    'Pause, Skip or swap Anytime',
    'Never run out, stay consistent',
    'Free UK Tracked Delivery',
    'Cancel Anytime',
  ];

  const CHECKMARK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M5.5 9.5L2.75 6.75L1.75 7.75L5.5 11.5L12.25 4.75L11.25 3.75L5.5 9.5Z" fill="currentColor"/></svg>';

  /**
   * @param {HTMLElement} element
   * @param {string} value
   */
  function setTextIfNeeded(element, value) {
    if (element.textContent !== value) {
      element.textContent = value;
    }
  }

  /**
   * @param {HTMLElement} element
   * @param {string} property
   * @param {string} value
   */
  function setStylePropertyIfNeeded(element, property, value) {
    if (element.style.getPropertyValue(property) !== value) {
      element.style.setProperty(property, value);
    }
  }

  /**
   * @param {HTMLElement} element
   * @param {string} className
   */
  function addClassIfNeeded(element, className) {
    if (!element.classList.contains(className)) {
      element.classList.add(className);
    }
  }

  /**
   * @returns {boolean}
   */
  function isEnhancementSettled() {
    const buyArea = document.querySelector(BUY_AREA_SELECTOR);
    const container = findSealContainer(document);

    return (
      buyArea instanceof HTMLElement &&
      buyArea.getAttribute('data-glowwa-seal-ready') === 'true' &&
      container instanceof HTMLElement &&
      container.dataset.glowwaSealEnhanced === 'true' &&
      hasPurchaseOptions(container)
    );
  }

  /**
   * @returns {string | null}
   */
  function getSealScriptSrc() {
    const fromDom = document.querySelector(`script[src*="${SEAL_SCRIPT_MATCH}"]`);
    if (fromDom instanceof HTMLScriptElement && fromDom.src) {
      return fromDom.src;
    }

    const fromConfig = document.getElementById('glowwa-seal-bootstrap');
    if (fromConfig instanceof HTMLElement) {
      const src = fromConfig.getAttribute('data-seal-script-src');
      if (typeof src === 'string' && src.trim() !== '') {
        return src.trim();
      }
    }

    return null;
  }

  /**
   * Theme-owned bootstrap: start loading Seal if the app embed has not yet.
   */
  function ensureSealScriptLoading() {
    if (document.querySelector(`script[src*="${SEAL_SCRIPT_MATCH}"]`)) {
      return;
    }

    const src = getSealScriptSrc();
    if (!src) {
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.async = true;
    document.head.append(script);
  }

  /**
   * @param {HTMLElement} buyArea
   * @param {ReturnType<typeof getPdpConfig>} config
   */
  function applySealThemeColors(buyArea, config) {
    const border =
      typeof config.glowwaOptionBorder === 'string'
        ? config.glowwaOptionBorder.trim()
        : '';

    if (border !== '') {
      setStylePropertyIfNeeded(buyArea, '--glowwa-seal-option-border', border);
    }

    const buyButtons = buyArea.querySelector('.buy-buttons-block');
    if (buyButtons instanceof HTMLElement && border !== '') {
      setStylePropertyIfNeeded(
        buyButtons,
        '--glowwa-seal-option-border',
        border,
      );
    }

    const sealRoot = buyArea.querySelector('.sealsubs-container');
    if (sealRoot instanceof HTMLElement && border !== '') {
      setStylePropertyIfNeeded(
        sealRoot,
        '--glowwa-seal-option-border',
        border,
      );
    }
  }

  /**
   * @returns {{ otpSubline?: string, capText?: string, benefits?: string[] | null, glowwaOptionBorder?: string, supplyDays?: number | null }}
   */
  function getPdpConfig() {
    const el = document.getElementById('glowwa-seal-pdp-config');
    if (!(el instanceof HTMLScriptElement) || !el.textContent) {
      return {};
    }

    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLElement} container
   * @returns {Record<string, unknown> | null}
   */
  function parseSealProductData(container) {
    const root =
      container.closest('.sealsubs-target-element.sealsubs-full') ||
      container.closest('.sealsubs-target-element[data-product]') ||
      container.closest('.sealsubs-target-element');

    const raw = root?.getAttribute('data-product');
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * @param {ReturnType<typeof parseSealProductData>} sealData
   */
  function getSellingPlans(sealData) {
    const group = sealData?.selling_plan_groups?.[0];
    return group?.selling_plans ?? [];
  }

  /**
   * @param {HTMLElement | null} container
   * @returns {HTMLElement | null}
   */
  function findSubscriptionBuyArea(container) {
    if (container instanceof HTMLElement) {
      const fromContainer = container.closest(BUY_AREA_SELECTOR);
      if (fromContainer instanceof HTMLElement) {
        return fromContainer;
      }
    }

    const match = document.querySelector(BUY_AREA_SELECTOR);
    return match instanceof HTMLElement ? match : null;
  }

  /**
   * @param {HTMLElement} buyArea
   */
  function markBuyAreaInteractive(buyArea) {
    buyArea.setAttribute('data-glowwa-seal-ready', 'true');
  }

  /**
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function hasPurchaseOptions(container) {
    return (
      container.querySelector('.sls-option-container[data-selling-plan-group]') !==
        null ||
      container.querySelector(
        '.sls-option-container[data-one-time-option-radio]',
      ) !== null ||
      container.querySelector('input.sls-option[data-type="subscription"]') !==
        null ||
      container.querySelector('input.sls-option[data-type="one_time"]') !== null
    );
  }

  /**
   * @param {Element | Document} root
   * @returns {HTMLElement | null}
   */
  function findSealContainer(root) {
    const subscriptionArea = root.querySelector(BUY_AREA_SELECTOR);
    const scopeRoot =
      subscriptionArea instanceof HTMLElement ? subscriptionArea : root;
    const scope =
      scopeRoot.querySelector('.hw-buy-area .sealsubs-container') ||
      scopeRoot.querySelector('.buy-buttons-block .sealsubs-container') ||
      scopeRoot.querySelector('.sealsubs-container');

    return scope instanceof HTMLElement ? scope : null;
  }

  /**
   * Single source of truth for every element that should be treated as
   * "the buy area" for class-toggling purposes (hw-buy-area,
   * hw-buy-area--multi-plan, hw-buy-area--plan-locked). markBuyAreaScope,
   * applyMultiPlanMode, and applyPlanLockedMode used to each compute their
   * own scope set and quietly drifted apart: markBuyAreaScope tagged
   * buyButtons.parentElement with hw-buy-area, but the multi-plan/plan-locked
   * toggles never included that same element. Since
   * `.hw-buy-area:not(.hw-buy-area--multi-plan) .sls-selling-plan-group-options`
   * is `!important` in the CSS, that untagged-but-hw-buy-area wrapper
   * silently won the cascade and hid the interval picker even on products
   * that should show it (e.g. a 15-day supply product with no matching
   * lock plan) — found by checking the Collagen Creamer 15-day product live,
   * not by inspection alone. Route every caller through this one function so
   * the three classes can never diverge again.
   * @param {HTMLElement} container
   * @returns {Set<HTMLElement>}
   */
  function getBuyAreaScopeElements(container) {
    const scopes = new Set();

    const buyArea =
      container.closest(BUY_AREA_SELECTOR) || container.closest('.hw-buy-area');
    const buyButtons = container.closest('.buy-buttons-block');

    if (buyArea instanceof HTMLElement) {
      scopes.add(buyArea);
    }

    if (buyButtons instanceof HTMLElement) {
      scopes.add(buyButtons);

      const stepWrapper = buyButtons.parentElement;
      if (stepWrapper instanceof HTMLElement) {
        scopes.add(stepWrapper);
      }

      const pinkGroup = buyButtons.closest(
        '.hw-group[style*="f2dce5"], .hw-group[style*="242, 220, 229"]',
      );
      if (pinkGroup instanceof HTMLElement) {
        scopes.add(pinkGroup);
      }
    }

    return scopes;
  }

  /**
   * @param {HTMLElement} container
   */
  function markBuyAreaScope(container) {
    getBuyAreaScopeElements(container).forEach((el) => {
      addClassIfNeeded(el, 'hw-buy-area');
    });
  }

  /**
   * @param {HTMLElement} container
   */
  function tagOptionCards(container) {
    const otpInput = container.querySelector('input.sls-option[data-type="one_time"]');
    const otp =
      container.querySelector('.sls-option-container[data-one-time-option-radio]') ||
      (otpInput instanceof HTMLInputElement
        ? otpInput.closest('.sls-option-container')
        : null);
    const sub = container.querySelector(
      '.sls-option-container[data-selling-plan-group]',
    );

    if (otp instanceof HTMLElement) {
      addClassIfNeeded(otp, 'hw-buy-area__option--onetime');
    }

    if (sub instanceof HTMLElement) {
      addClassIfNeeded(sub, 'hw-buy-area__option--subscription');
    }
  }

  /**
   * Show Seal interval select only when this product has multiple selling plans.
   * @param {HTMLElement} container
   * @param {ReturnType<typeof parseSealProductData>} sealData
   */
  function applyMultiPlanMode(container, sealData) {
    const planCount = getSellingPlans(sealData).length;
    const multi = planCount > 1;

    getBuyAreaScopeElements(container).forEach((el) => {
      el.classList.toggle('hw-buy-area--multi-plan', multi);
    });
  }

  /**
   * Parses a Seal plan interval label ("Month", "2 weeks", "3 Months",
   * "6 months") into an approximate day count. No lookup table: this is
   * whatever text Seal itself returns for the plan, so it stays correct if
   * Seal's plan set ever changes (new interval added/renamed) without any
   * theme code change. Returns null for text it can't parse.
   * @param {unknown} text
   * @returns {number | null}
   */
  function parseIntervalToDays(text) {
    if (typeof text !== 'string') {
      return null;
    }

    const match = text.trim().match(/^(\d+)?\s*(day|week|month|year)s?$/i);
    if (!match) {
      return null;
    }

    const count = match[1] ? parseInt(match[1], 10) : 1;
    const unitDays = { day: 1, week: 7, month: 30, year: 365 };
    const unit = match[2].toLowerCase();

    return count * unitDays[unit];
  }

  /**
   * @param {ReturnType<typeof getSellingPlans>} plans
   * @param {number} supplyDays
   * @returns {string | null} the id of whichever plan's parsed interval is
   *   numerically closest to supplyDays, or null if no plan parsed.
   */
  function findNearestPlanId(plans, supplyDays) {
    let bestId = null;
    let bestDiff = Infinity;

    plans.forEach((plan) => {
      const value = plan.options && plan.options[0] ? plan.options[0].value : undefined;
      const days = parseIntervalToDays(value);

      if (days === null) {
        return;
      }

      const diff = Math.abs(days - supplyDays);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestId = String(plan.id);
      }
    });

    return bestId;
  }

  /**
   * Force-select whichever Seal selling plan is numerically closest to this
   * product's real supply length (glowwa-seal-pdp-config resolves supplyDays
   * server-side from custom.supply_picker/custom.supply -- theme data Seal
   * has no way to know on its own). Glowwa's Seal group exposes the same
   * Month / 2 weeks / 3 Months / 6 months plans on every product and defaults
   * to Month regardless of supply size -- left alone, a customer on a
   * 6-month-supply product could subscribe to 6 months of product delivered
   * every month. Deliberately no static day-to-plan-id table: matching is
   * computed at runtime from Seal's own live plan text, so it generalizes to
   * any supply length (e.g. a future 120-day product) and any future Seal
   * plan without needing a maintained table entry. Returns true when the
   * lock was applied (or already matches), so the caller can hide the
   * interval picker via CSS; returns false when there's nothing to enforce
   * (supply length unresolved, or no plan text was parseable), so the
   * existing multi-plan fallback still shows the picker instead of silently
   * locking to the wrong interval.
   * @param {HTMLElement} container
   * @param {ReturnType<typeof parseSealProductData>} sealData
   * @param {ReturnType<typeof getPdpConfig>} config
   * @returns {boolean}
   */
  function enforceNearestPlan(container, sealData, config) {
    const supplyDays =
      typeof config.supplyDays === 'number' && Number.isFinite(config.supplyDays) && config.supplyDays > 0
        ? config.supplyDays
        : null;

    if (supplyDays === null) {
      return false;
    }

    const plans = getSellingPlans(sealData);
    const targetId = findNearestPlanId(plans, supplyDays);

    if (targetId === null) {
      return false;
    }

    const select = container.querySelector('select[name="subs_interval"]');
    if (!(select instanceof HTMLSelectElement)) {
      return false;
    }

    const hasOption = Array.from(select.options).some(
      (option) => option.value === targetId,
    );

    if (!hasOption) {
      return false;
    }

    if (container.dataset.glowwaTargetPlanApplied === targetId) {
      return true;
    }

    if (select.value !== targetId) {
      select.value = targetId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    container.dataset.glowwaTargetPlanApplied = targetId;
    return true;
  }

  /**
   * @param {HTMLElement} container
   * @param {boolean} locked
   */
  function applyPlanLockedMode(container, locked) {
    getBuyAreaScopeElements(container).forEach((el) => {
      el.classList.toggle('hw-buy-area--plan-locked', locked);
    });
  }

  /**
   * Sync subscribe card title + pink strip from Seal offer data (not hardcoded).
   * @param {HTMLElement} container
   * @param {ReturnType<typeof parseSealProductData>} sealData
   */
  function syncSubscriptionFromSeal(container, sealData) {
    const plans = getSellingPlans(sealData);
    const sub = container.querySelector('.hw-buy-area__option--subscription');

    if (!(sub instanceof HTMLElement) || plans.length === 0) {
      return;
    }

    const label = sub.querySelector('.sls-selling-plan-group-name');
    const desc = sub.querySelector('.sls-selling-plan-description-content');
    const intervalSelect = sub.querySelector('select[name="subs_interval"]');
    const sellingPlanInput = container.querySelector('input[name="selling_plan"]');

    let planId = sellingPlanInput instanceof HTMLInputElement ? sellingPlanInput.value : '';

    if (!planId && intervalSelect instanceof HTMLSelectElement) {
      planId = intervalSelect.value;
    }

    const plan =
      plans.find((p) => String(p.id) === String(planId)) ||
      plans.find((p) => intervalSelect instanceof HTMLSelectElement && p.id === intervalSelect.value) ||
      plans[0];

    if (label instanceof HTMLElement && plan?.name) {
      setTextIfNeeded(label, plan.name);
    }

    if (desc instanceof HTMLElement && plan?.description) {
      const text = plan.description.trim();
      if (text !== '') {
        setTextIfNeeded(desc, text);
        if (desc.hidden) {
          desc.hidden = false;
        }
      }
    }
  }

  /**
   * Move delivery strip to .seal-row so it spans the card (not only the label column).
   * @param {HTMLElement} sub
   */
  function relocateSubscriptionDescription(sub) {
    const row = sub.querySelector('.seal-row');
    const desc = sub.querySelector('.sls-selling-plan-description-content');

    if (!(row instanceof HTMLElement) || !(desc instanceof HTMLElement)) {
      return;
    }

    addClassIfNeeded(desc, 'hw-buy-area__seal-description--full-width');

    if (desc.parentElement !== row) {
      const insertBefore =
        row.querySelector('.hw-buy-area__seal-divider') ||
        row.querySelector('.hw-buy-area__seal-benefits');

      if (insertBefore instanceof HTMLElement) {
        row.insertBefore(desc, insertBefore);
      } else {
        row.append(desc);
      }

      sub.dataset.glowwaSealDescRelocated = 'true';
    }

    const wrapper = sub.querySelector('.sls-selling-plan-description');
    if (
      wrapper instanceof HTMLElement &&
      wrapper !== desc &&
      wrapper.childElementCount === 0 &&
      sub.dataset.glowwaSealDescWrapperRemoved !== 'true'
    ) {
      wrapper.remove();
      sub.dataset.glowwaSealDescWrapperRemoved = 'true';
    }
  }

  /**
   * @param {HTMLElement} container
   * @param {ReturnType<typeof getPdpConfig>} config
   */
  function injectRecommendedCap(container, config) {
    const sub = container.querySelector('.hw-buy-area__option--subscription');
    if (!(sub instanceof HTMLElement)) {
      return;
    }

    const showCap = config.showCap !== false;
    const prev = sub.previousElementSibling;

    if (prev?.classList.contains('hw-buy-area__seal-cap')) {
      if (!showCap) {
        prev.remove();
      } else {
        setTextIfNeeded(
          prev,
          config.capText || 'RECOMMENDED FOR CONSISTENT RESULTS',
        );
      }
      return;
    }

    if (!showCap) {
      return;
    }

    const cap = document.createElement('div');
    cap.className = 'hw-buy-area__seal-cap';
    cap.textContent = config.capText || 'RECOMMENDED FOR CONSISTENT RESULTS';
    sub.parentNode?.insertBefore(cap, sub);
  }

  /**
   * @param {HTMLElement} container
   * @param {ReturnType<typeof getPdpConfig>} config
   */
  function injectBenefits(container, config) {
    const sub = container.querySelector('.hw-buy-area__option--subscription');
    if (!(sub instanceof HTMLElement)) {
      return;
    }

    const benefits =
      Array.isArray(config.benefits) && config.benefits.length > 0
        ? config.benefits.filter((line) => typeof line === 'string' && line.trim() !== '')
        : DEFAULT_BENEFITS;

    let list = sub.querySelector('.hw-buy-area__seal-benefits');

    if (!(list instanceof HTMLElement)) {
      const row = sub.querySelector('.seal-row');
      const anchor =
        sub.querySelector('.sls-selling-plan-description-content') ||
        sub.querySelector('.sls-label-container');

      if (!(row instanceof HTMLElement) && !(anchor instanceof HTMLElement)) {
        return;
      }

      const divider = document.createElement('hr');
      divider.className = 'hw-buy-area__seal-divider';

      list = document.createElement('ul');
      list.className = 'hw-buy-area__seal-benefits';

      if (row instanceof HTMLElement) {
        row.append(divider, list);
      } else if (anchor instanceof HTMLElement) {
        anchor.insertAdjacentElement('afterend', divider);
        divider.insertAdjacentElement('afterend', list);
      }
    }

    const signature = benefits.join('\u0001');
    if (list.dataset.glowwaBenefitsSignature === signature) {
      return;
    }

    list.dataset.glowwaBenefitsSignature = signature;
    list.replaceChildren();

    benefits.forEach((text) => {
      if (typeof text !== 'string' || text.trim() === '') {
        return;
      }

      const item = document.createElement('li');
      item.className = 'hw-buy-area__seal-benefit';

      const icon = document.createElement('span');
      icon.className = 'hw-buy-area__seal-benefit-icon';
      icon.innerHTML = CHECKMARK_SVG;

      const label = document.createElement('span');
      label.textContent = text.trim();

      item.append(icon, label);
      list.append(item);
    });
  }

  /**
   * @param {HTMLElement} container
   * @param {ReturnType<typeof getPdpConfig>} config
   */
  function injectOtpSubline(container, config) {
    const otp = container.querySelector('.hw-buy-area__option--onetime');
    const sublineText = config.otpSubline || '30 day supply delivered once';

    if (!(otp instanceof HTMLElement)) {
      return;
    }

    const label = otp.querySelector('.sls-selling-plan-group-name');
    if (!(label instanceof HTMLElement)) {
      return;
    }

    let subline = otp.querySelector('.hw-buy-area__otp-subline');

    if (!(subline instanceof HTMLElement)) {
      let wrap = otp.querySelector('.hw-buy-area__otp-subline-wrap');

      if (!(wrap instanceof HTMLElement)) {
        wrap = document.createElement('div');
        wrap.className = 'hw-buy-area__otp-subline-wrap';
        label.parentNode?.insertBefore(wrap, label);
        wrap.append(label);
      }

      subline = document.createElement('p');
      subline.className = 'hw-buy-area__otp-subline';
      wrap.append(subline);
    }

    setTextIfNeeded(subline, sublineText);
  }

  /**
   * @param {HTMLElement} container
   */
  function bindSealChangeHandlers(container) {
    if (container.dataset.glowwaListenersBound === 'true') {
      return;
    }

    container.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (
        target.matches('select[name="subs_interval"]') ||
        target.matches('input.sls-option') ||
        target.matches('input[name="selling_plan"]')
      ) {
        syncSubscriptionFromSeal(container, parseSealProductData(container));
      }
    });

    container.dataset.glowwaListenersBound = 'true';
  }

  /**
   * @param {HTMLElement} container
   */
  function ensureSubscriptionDefault(container, config) {
    if (config.defaultToSubscription === false) {
      return;
    }

    if (container.dataset.glowwaDefaultApplied === 'true') {
      return;
    }

    const subInput = container.querySelector('input.sls-option[data-type="subscription"]');

    if (!(subInput instanceof HTMLInputElement)) {
      return;
    }

    if (subInput.checked) {
      container.dataset.glowwaDefaultApplied = 'true';
      return;
    }

    const target =
      subInput.closest('[role="radio"]') || subInput.parentElement || subInput;

    if (target instanceof HTMLElement) {
      target.click();
    } else {
      subInput.click();
    }

    container.dataset.glowwaDefaultApplied = 'true';
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLElement | null} buyArea
   */
  function revealWhenSealPresent(container, buyArea) {
    if (!(buyArea instanceof HTMLElement)) {
      return;
    }

    if (buyArea.getAttribute('data-glowwa-seal-ready') === 'true') {
      return;
    }

    if (!hasPurchaseOptions(container)) {
      return;
    }

    markBuyAreaInteractive(buyArea);
  }

  /**
   * Cosmetic half of enhancement: copy/classes only, never forces Seal's own
   * script to react (no synthetic `change`/`click`). Safe and cheap to run
   * on every observed mutation with no wait — every step here is idempotent
   * (setTextIfNeeded/addClassIfNeeded/dataset guards), so re-running it
   * while Seal is still mid-build just quietly catches up, and this is what
   * makes cap/benefits/label/multi-plan-display appear in sync with Seal's
   * widget instead of flashing raw.
   * @param {HTMLElement} container
   * @param {HTMLElement | null} buyArea
   * @param {ReturnType<typeof getPdpConfig>} config
   * @param {ReturnType<typeof parseSealProductData>} sealData
   */
  function applyCosmeticEnhancements(container, buyArea, config, sealData) {
    tagOptionCards(container);
    revealWhenSealPresent(container, buyArea);

    if (buyArea instanceof HTMLElement) {
      applySealThemeColors(buyArea, config);
    }

    markBuyAreaScope(container);
    applyMultiPlanMode(container, sealData);
    syncSubscriptionFromSeal(container, sealData);
    injectRecommendedCap(container, config);
    injectOtpSubline(container, config);
    injectBenefits(container, config);

    const sub = container.querySelector('.hw-buy-area__option--subscription');
    if (sub instanceof HTMLElement) {
      relocateSubscriptionDescription(sub);
    }

    bindSealChangeHandlers(container);
  }

  /**
   * Reactive half of enhancement: force-selecting the interval-matched plan
   * and defaulting to "subscribe" both hand control to Seal's own script
   * (the synthetic `change`/`click` events it listens for — see
   * enforceNearestPlan()/ensureSubscriptionDefault() above), which may
   * recompute or re-render price. Running this before Seal has finished
   * building its own widget makes Seal do that work against a still-forming
   * DOM, competing with its own remaining setup — tried running this
   * (bundled with the cosmetic half) immediately/synchronously on first
   * sight of Seal to kill the raw-widget flash, and it made real-world load
   * noticeably slower, exactly because of this race. Kept behind the
   * settle-detecting debounce in schedulePlanEnforcement() below so it only
   * fires once Seal's own mutations have actually gone quiet, same as
   * before that attempt — only the cosmetic half above was pulled out to
   * run eagerly.
   * @param {HTMLElement} container
   * @param {ReturnType<typeof parseSealProductData>} sealData
   * @param {ReturnType<typeof getPdpConfig>} config
   */
  function applyPlanEnforcement(container, sealData, config) {
    const planLocked = enforceNearestPlan(container, sealData, config);
    applyPlanLockedMode(container, planLocked);

    if (container.dataset.glowwaSealEnhanced !== 'true') {
      ensureSubscriptionDefault(container, config);
      container.dataset.glowwaSealEnhanced = 'true';
    }
  }

  function enhanceContainer(container) {
    const buyArea = findSubscriptionBuyArea(container);

    isEnhancing = true;

    try {
      const config = getPdpConfig();
      const sealData = parseSealProductData(container);

      applyCosmeticEnhancements(container, buyArea, config, sealData);
    } finally {
      isEnhancing = false;
    }

    maybeDisconnectObservers();
  }

  /**
   * @param {Element | Document} root
   */
  function runEnhance(root) {
    const container = findSealContainer(root);
    if (container) {
      enhanceContainer(container);
    }
  }

  /**
   * How long to wait, after the buy area's DOM has gone quiet, before
   * forcing the interval-lock/default-plan selection on Seal. This is a
   * genuine settle-detector, not a flash-prevention delay (the cosmetic
   * pass above already handles that eagerly) — it exists purely so we don't
   * hand Seal a synthetic `change`/`click` to react to while it's still
   * mid-construction. Tune while watching a real product page: too short
   * risks the race described on applyPlanEnforcement() above; too long
   * delays how quickly the correct plan/price lock in.
   */
  const PLAN_ENFORCEMENT_DEBOUNCE_MS = 80;

  function schedulePlanEnforcement() {
    const container = findSealContainer(document);
    if (!(container instanceof HTMLElement)) {
      return;
    }

    if (container.dataset.glowwaSealEnhanced === 'true') {
      return;
    }

    if (debounceTimer !== null) {
      window.clearTimeout(debounceTimer);
    }

    debounceTimer = window.setTimeout(() => {
      debounceTimer = null;

      const settledContainer = findSealContainer(document);
      if (!(settledContainer instanceof HTMLElement)) {
        return;
      }

      const config = getPdpConfig();
      const sealData = parseSealProductData(settledContainer);
      applyPlanEnforcement(settledContainer, sealData, config);
      maybeDisconnectObservers();
    }, PLAN_ENFORCEMENT_DEBOUNCE_MS);
  }

  function scheduleEnhance() {
    if (isEnhancementSettled()) {
      maybeDisconnectObservers();
      return;
    }

    // Cosmetic pass runs immediately every time — cheap, idempotent, and
    // never races Seal. The reactive plan-enforcement pass is scheduled
    // separately below and waits for Seal to settle.
    runEnhance(document);
    schedulePlanEnforcement();
  }

  function maybeDisconnectObservers() {
    if (!isEnhancementSettled()) {
      return;
    }

    disconnectObservers();
  }

  function initObserver() {
    document.querySelectorAll(BUY_AREA_SELECTOR).forEach((root) => {
      if (!(root instanceof HTMLElement)) {
        return;
      }

      if (observedRoots.has(root)) {
        return;
      }

      observedRoots.add(root);

      const observer = new MutationObserver(() => {
        if (isEnhancing || isEnhancementSettled()) {
          return;
        }

        scheduleEnhance();
      });

      observer.observe(root, { childList: true, subtree: true });
      observers.push(observer);
    });
  }

  /**
   * Poll until Seal injects or the customer navigates away.
   * Observers alone miss injections when Seal mutates outside the buy-area subtree.
   */
  function startSealPoll() {
    if (sealPollTimer !== null) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 120;

    sealPollTimer = window.setInterval(() => {
      attempts += 1;

      const buyArea = document.querySelector(BUY_AREA_SELECTOR);
      if (!(buyArea instanceof HTMLElement)) {
        if (attempts >= maxAttempts) {
          window.clearInterval(sealPollTimer);
          sealPollTimer = null;
        }
        return;
      }

      if (buyArea.getAttribute('data-glowwa-seal-ready') === 'true') {
        window.clearInterval(sealPollTimer);
        sealPollTimer = null;
        maybeDisconnectObservers();
        return;
      }

      runEnhance(document);

      if (buyArea.getAttribute('data-glowwa-seal-ready') === 'true') {
        window.clearInterval(sealPollTimer);
        sealPollTimer = null;
        maybeDisconnectObservers();
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(sealPollTimer);
        sealPollTimer = null;
      }
    }, 500);
  }

  function disconnectObservers() {
    while (observers.length > 0) {
      const observer = observers.pop();
      observer?.disconnect();
    }
    observedRoots.clear();
  }

  function resetSealPoll() {
    if (sealPollTimer !== null) {
      window.clearInterval(sealPollTimer);
      sealPollTimer = null;
    }
  }

  function init() {
    ensureSealScriptLoading();
    disconnectObservers();
    initObserver();
    scheduleEnhance();
    resetSealPoll();
    startSealPoll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }

      const section = event.target;
      const affectsBuyArea =
        section.querySelector(
          '.glowwa-pdp-buy-area, .glowwa-pdp-buy-subscription, [data-glowwa-buy-mode]',
        ) !== null;

      if (!affectsBuyArea) {
        return;
      }

      init();
    },
    { passive: true },
  );
})();
